import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Trash2,
  Phone,
  Lock,
  LogOut,
  KeyRound,
  Loader2,
  AlertCircle,
  CheckCircle2,
  X,
  RefreshCw,
} from 'lucide-react';
import { api, getToken, setToken, clearToken } from '../lib/api';

const LoginForm = ({ onSuccess }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const { data } = await api.post('/staff/login', { password });
      setToken(data.token);
      onSuccess(data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Login failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f4ead4] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-[#2a1a0f] hover:opacity-70 mb-8">
          <ArrowLeft className="w-4 h-4" />
          <span className="text-[14px]">Back to site</span>
        </Link>
        <div className="bg-[#ecdcbf] rounded-3xl p-8 sm:p-10">
          <div className="w-12 h-12 rounded-full bg-[#2a1a0f] flex items-center justify-center mb-5">
            <Lock className="w-5 h-5 text-[#f4ead4]" strokeWidth={1.8} />
          </div>
          <h1 className="font-serif text-[32px] text-[#2a1a0f] leading-tight">Staff sign-in</h1>
          <p className="mt-2 text-[14px] text-[#4a3524]">
            Only Bliss Coffee staff can view incoming pre-orders.
          </p>

          <form onSubmit={submit} className="mt-6 space-y-3">
            <input
              type="password"
              required
              value={password}
              autoFocus
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Staff password"
              className="w-full bg-[#f4ead4] border border-[#2a1a0f]/15 rounded-xl px-4 py-3 text-[14px] text-[#2a1a0f] placeholder:text-[#7a5c40] focus:outline-none focus:border-[#b8532a] transition-colors"
            />
            {error && (
              <div className="flex items-start gap-2 bg-[#f8d7d0] border border-[#b8532a]/30 text-[#8a3312] rounded-xl px-4 py-3 text-[13px]">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
            <button
              type="submit"
              disabled={busy}
              className="w-full bg-[#b8532a] hover:bg-[#a34620] disabled:bg-[#b8532a]/50 text-[#f4ead4] py-3.5 rounded-full text-[15px] font-medium transition-colors inline-flex items-center justify-center gap-2"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

const ChangePasswordModal = ({ open, onClose, ownerEmail, setOwnerEmail }) => {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [email, setEmail] = useState(ownerEmail || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (open) {
      setCurrent('');
      setNext('');
      setConfirm('');
      setEmail(ownerEmail || '');
      setError('');
      setSuccess('');
    }
  }, [open, ownerEmail]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setError('');
    setSuccess('');
    if (next.length < 6) return setError('New password must be at least 6 characters');
    if (next !== confirm) return setError('Passwords do not match');
    setBusy(true);
    try {
      const { data } = await api.post('/staff/change-password', {
        currentPassword: current,
        newPassword: next,
        ownerEmail: email || undefined,
      });
      setOwnerEmail(data.ownerEmail);
      setSuccess(
        data.emailDelivered
          ? `Password changed. A notification was emailed to ${data.ownerEmail}.`
          : `Password changed. A notification was stored for ${data.ownerEmail} (email service not configured yet).`
      );
      setCurrent('');
      setNext('');
      setConfirm('');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not change password');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#2a1a0f]/60 backdrop-blur-sm flex items-center justify-center px-4">
      <div className="bg-[#f4ead4] rounded-3xl w-full max-w-md p-8 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-[#7a5c40] hover:text-[#2a1a0f]"
        >
          <X className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-[#2a1a0f] flex items-center justify-center">
            <KeyRound className="w-4 h-4 text-[#f4ead4]" />
          </div>
          <h2 className="font-serif text-[24px] text-[#2a1a0f]">Change password</h2>
        </div>
        <p className="text-[13px] text-[#4a3524] mb-5">
          A confirmation is sent to the owner email below whenever the password is changed.
        </p>

        <form onSubmit={submit} className="space-y-3">
          <input
            type="password"
            required
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            placeholder="Current password"
            className="w-full bg-[#ecdcbf] border border-[#2a1a0f]/15 rounded-xl px-4 py-3 text-[14px] text-[#2a1a0f] placeholder:text-[#7a5c40] focus:outline-none focus:border-[#b8532a]"
          />
          <input
            type="password"
            required
            value={next}
            onChange={(e) => setNext(e.target.value)}
            placeholder="New password (min 6 characters)"
            className="w-full bg-[#ecdcbf] border border-[#2a1a0f]/15 rounded-xl px-4 py-3 text-[14px] text-[#2a1a0f] placeholder:text-[#7a5c40] focus:outline-none focus:border-[#b8532a]"
          />
          <input
            type="password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Confirm new password"
            className="w-full bg-[#ecdcbf] border border-[#2a1a0f]/15 rounded-xl px-4 py-3 text-[14px] text-[#2a1a0f] placeholder:text-[#7a5c40] focus:outline-none focus:border-[#b8532a]"
          />
          <div>
            <label className="block text-[12px] text-[#7a5c40] mb-1">
              Owner email (receives change notification)
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="owner@blissquesnel.ca"
              className="w-full bg-[#ecdcbf] border border-[#2a1a0f]/15 rounded-xl px-4 py-3 text-[14px] text-[#2a1a0f] placeholder:text-[#7a5c40] focus:outline-none focus:border-[#b8532a]"
            />
          </div>

          {error && (
            <div className="flex items-start gap-2 bg-[#f8d7d0] border border-[#b8532a]/30 text-[#8a3312] rounded-xl px-4 py-3 text-[13px]">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
          {success && (
            <div className="flex items-start gap-2 bg-[#e8f0dc] border border-[#6e8b3d]/30 text-[#3e5a1f] rounded-xl px-4 py-3 text-[13px]">
              <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{success}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            className="w-full bg-[#2a1a0f] hover:bg-[#3d2817] disabled:opacity-60 text-[#f4ead4] py-3 rounded-full text-[14px] inline-flex items-center justify-center gap-2"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {busy ? 'Saving…' : 'Save new password'}
          </button>
        </form>
      </div>
    </div>
  );
};

const StaffPage = () => {
  const [authed, setAuthed] = useState(!!getToken());
  const [orders, setOrders] = useState([]);
  const [ownerEmail, setOwnerEmail] = useState('');
  const [showChange, setShowChange] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    if (!getToken()) {
      setAuthed(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const [ordersRes, meRes] = await Promise.all([
        api.get('/staff/orders'),
        api.get('/staff/me'),
      ]);
      setOrders(ordersRes.data);
      setOwnerEmail(meRes.data.ownerEmail || '');
    } catch (err) {
      if (err?.response?.status === 401) {
        setAuthed(false);
      } else {
        setError('Could not load orders.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authed) {
      load();
      const t = setInterval(load, 5000);
      return () => clearInterval(t);
    }
  }, [authed]); // eslint-disable-line react-hooks/exhaustive-deps

  const logout = () => {
    clearToken();
    setAuthed(false);
    setOrders([]);
  };

  const markDone = async (id) => {
    try {
      await api.patch(`/staff/orders/${id}`, { status: 'done' });
      load();
    } catch {
      /* ignore */
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/staff/orders/${id}`);
      load();
    } catch {
      /* ignore */
    }
  };

  if (!authed) return <LoginForm onSuccess={() => setAuthed(true)} />;

  return (
    <div className="min-h-screen bg-[#f4ead4]">
      <header className="sticky top-0 z-40 bg-[#f4ead4]/95 backdrop-blur-md border-b border-[#2a1a0f]/10">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between gap-3">
          <Link to="/" className="inline-flex items-center gap-2 text-[#2a1a0f] hover:opacity-70">
            <ArrowLeft className="w-4 h-4" strokeWidth={1.8} />
            <span className="text-[15px]">Back</span>
          </Link>
          <span className="font-serif text-[19px] text-[#2a1a0f] hidden sm:inline">
            Staff · Orders
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              disabled={loading}
              className="inline-flex items-center gap-1.5 bg-[#f4ead4] border border-[#2a1a0f]/15 hover:bg-[#ecdcbf] text-[#2a1a0f] px-3 py-2 rounded-full text-[13px]"
              title="Refresh"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => setShowChange(true)}
              className="inline-flex items-center gap-1.5 bg-[#f4ead4] border border-[#2a1a0f]/15 hover:bg-[#ecdcbf] text-[#2a1a0f] px-3 py-2 rounded-full text-[13px]"
            >
              <KeyRound className="w-3.5 h-3.5" /> Change password
            </button>
            <button
              onClick={logout}
              className="inline-flex items-center gap-1.5 bg-[#2a1a0f] text-[#f4ead4] hover:bg-[#3d2817] px-3 py-2 rounded-full text-[13px]"
            >
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-10">
        <h1 className="font-serif text-[38px] text-[#2a1a0f] mb-2">Incoming pre-orders</h1>
        <p className="text-[14px] text-[#7a5c40] mb-8">
          Live queue — auto-refreshes every 5 seconds. {ownerEmail && `Owner: ${ownerEmail}`}
        </p>

        {error && (
          <div className="mb-6 flex items-start gap-2 bg-[#f8d7d0] border border-[#b8532a]/30 text-[#8a3312] rounded-xl px-4 py-3 text-[13px]">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {orders.length === 0 ? (
          <div className="bg-[#ecdcbf] rounded-2xl p-10 text-center">
            <p className="text-[15px] text-[#7a5c40]">
              {loading ? 'Loading…' : 'No orders yet. New orders will appear here.'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((o) => (
              <div
                key={o.id}
                className={`rounded-2xl p-6 border ${
                  o.status === 'done'
                    ? 'bg-[#ecdcbf]/50 border-[#2a1a0f]/10 opacity-70'
                    : 'bg-[#ecdcbf] border-[#2a1a0f]/10'
                }`}
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <span className="font-serif text-[22px] text-[#2a1a0f]">{o.id}</span>
                      {o.status === 'done' && (
                        <span className="text-[11px] uppercase tracking-wider bg-[#2a1a0f] text-[#f4ead4] px-2 py-0.5 rounded-full">
                          Done
                        </span>
                      )}
                    </div>
                    <div className="text-[14px] text-[#2a1a0f]">
                      {o.customerName} ·{' '}
                      <a href={`tel:${o.phone}`} className="underline hover:text-[#b8532a]">
                        <Phone className="w-3 h-3 inline mr-1" />
                        {o.phone}
                      </a>
                    </div>
                    <div className="text-[12px] text-[#7a5c40] mt-1">
                      {new Date(o.placedAt).toLocaleString()}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {o.status !== 'done' && (
                      <button
                        onClick={() => markDone(o.id)}
                        className="bg-[#b8532a] hover:bg-[#a34620] text-[#f4ead4] px-4 py-2 rounded-full text-[13px]"
                      >
                        Mark done
                      </button>
                    )}
                    <button
                      onClick={() => remove(o.id)}
                      className="text-[#7a5c40] hover:text-[#b8532a] p-2"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-[#2a1a0f]/10">
                  <ul className="space-y-1">
                    {o.items.map((it, i) => (
                      <li key={i} className="flex justify-between text-[14px] text-[#2a1a0f]">
                        <span>
                          {it.quantity}× {it.name}{' '}
                          <span className="text-[#7a5c40]">({it.category})</span>
                        </span>
                        <span className="text-[#b8532a]">{it.priceStr}</span>
                      </li>
                    ))}
                  </ul>
                  {o.notes && (
                    <div className="mt-3 text-[13px] italic text-[#4a3524]">“{o.notes}”</div>
                  )}
                  <div className="mt-3 pt-3 border-t border-[#2a1a0f]/10 flex justify-between">
                    <span className="text-[14px] text-[#2a1a0f]">Estimated total</span>
                    <span className="font-serif text-[18px] text-[#2a1a0f]">
                      ${Number(o.total).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ChangePasswordModal
        open={showChange}
        onClose={() => setShowChange(false)}
        ownerEmail={ownerEmail}
        setOwnerEmail={setOwnerEmail}
      />
    </div>
  );
};

export default StaffPage;
